// Centered modal dialog — ported from Luan's .dialog-overlay/.dialog. Portal-rendered, closes on
// backdrop click and Escape, locks body scroll while open. Portal-agnostic — shared across apps.
// Style contract: .dialog-overlay / .dialog (+ .wide) / .dialog-close live in styles.css.
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

const CloseIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function Modal({
  open,
  onClose,
  children,
  wide,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  labelledBy?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="dialog-overlay open" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog ${wide ? "wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        <button className="dialog-close" onClick={onClose} aria-label="Close">{CloseIcon}</button>
        {children}
      </div>
    </div>,
    document.body,
  );
}
